import {
  AgentQuestionEventDetail,
  AnswerValue,
  isDualRangeAnswer,
  isMultiFieldAnswer,
} from "@/types/agent"
import { cn } from "cn"

export function formatAnswerValue(value: AnswerValue): string {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No"
  }
  if (typeof value === "number") {
    return value.toString()
  }
  if (typeof value === "string") {
    return value
  }
  if (Array.isArray(value)) {
    return value.join(", ")
  }
  if (isDualRangeAnswer(value)) {
    return `${value.min} - ${value.max}`
  }
  if (isMultiFieldAnswer(value)) {
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ")
  }
  return String(value)
}

interface QuestionEventDisplayProps {
  event: AgentQuestionEventDetail
  isExpanded: boolean
  onToggle: () => void
}

export function QuestionEventDisplay({
  event,
  isExpanded,
  onToggle,
}: QuestionEventDisplayProps) {
  const questions = event.content.questions
  const answers = event.answers || {}

  let statusLabel: string
  let textColor: string

  switch (event.status) {
    case "answered":
      statusLabel = "Questions Answered"
      textColor = "text-green-10"
      break
    case "skipped":
      statusLabel = "Questions Skipped"
      textColor = "text-gray-11"
      break
    case "pending":
    default:
      statusLabel = "Questions Asked"
      textColor = "text-indigo-10"
      break
  }

  function renderCollapsed() {
    return (
      <div onClick={onToggle} className="group cursor-pointer">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-xs font-medium underline transition-colors group-hover:text-gray-12",
              textColor
            )}
          >
            {statusLabel}
          </span>
        </div>
      </div>
    )
  }

  function renderExpanded() {
    return (
      <div>
        <div
          className="group mb-2 flex cursor-pointer items-center gap-2"
          onClick={onToggle}
        >
          <span
            className={cn(
              "text-sm font-medium underline transition-colors group-hover:text-gray-12",
              textColor
            )}
          >
            {statusLabel}
          </span>
        </div>
        <div className="space-y-3 rounded-sm border border-gray-4 bg-subtle p-3 text-sm">
          {questions.map((q, idx) => {
            const inputTypeLabel =
              q.input_type === "single_select"
                ? "Single choice"
                : "Multiple choice"
            return (
              <div key={q.id} className="text-sm">
                <div className="font-medium text-gray-12">
                  <span>
                    {idx + 1}. {q.question_text}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-11">
                  <span>({inputTypeLabel})</span>
                </div>
                {q.option_groups?.length ? (
                  <div className="mt-1 ml-4 text-xs text-gray-11">
                    {q.option_groups.map((group) => (
                      <div key={group.label}>
                        <div className="mt-1 font-medium">{group.label}</div>
                        <ul className="ml-4 list-disc">
                          {group.options.map((opt, i) => (
                            <li key={i}>{opt}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-11">
                    {q.options.map((opt, i) => (
                      <li key={i}>{opt}</li>
                    ))}
                  </ul>
                )}
                {event.status === "answered" && answers[q.id] && (
                  <div className="mt-2 rounded-md bg-background p-2 text-xs">
                    <span className="font-medium text-gray-11">Answer: </span>
                    <span className="text-gray-12">
                      {formatAnswerValue(answers[q.id])}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (isExpanded) {
    return renderExpanded()
  }
  return renderCollapsed()
}
